import React from 'react';
import styled from 'styled-components';
import {useComponent} from '../../component-library';
import {SliderConstants} from './constants';
import {usePointerDownHandler, useThumbKeyHandlers} from './thumb-interaction';

type ThumbProps = Readonly<{
  value: number;
  ariaValueText?: string;
  railElementRef: React.RefObject<HTMLDivElement>;
  thumbMin: number;
  thumbMax: number;
  onSlidingChange: (isSliding: boolean) => void;
  onValueChange: (update: (old: number) => number) => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  isRtl?: boolean;
  position: 'top' | 'bottom';
  testId?: string;
}>;

export const Thumb = ({
  value,
  ariaValueText,
  railElementRef,
  onValueChange,
  onSlidingChange,
  thumbMin,
  thumbMax,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  isRtl = false,
  position,
  testId,
}: ThumbProps): JSX.Element => {
  const thumbElementRef = React.useRef<HTMLDivElement>(null);

  const pointerDownHandler = usePointerDownHandler(
    railElementRef,
    thumbElementRef,
    onSlidingChange,
    onValueChange,
    isRtl
  );

  const keyHandlers = useThumbKeyHandlers(
    onSlidingChange,
    onValueChange,
    isRtl
  );

  const Icon = useComponent('Icon');

  return (
    <StyledThumb
      tabIndex={0}
      ref={thumbElementRef}
      position={position}
      role="slider"
      data-testid={testId}
      aria-valuemax={thumbMax}
      aria-valuemin={thumbMin}
      aria-valuenow={value}
      aria-valuetext={ariaValueText}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onPointerDown={pointerDownHandler}
      {...keyHandlers}
    >
      <Icon name="Size" size="small" ariaHidden />
    </StyledThumb>
  );
};

interface StyledThumbProps {
  position: 'top' | 'bottom';
  testId?: string;
}

const StyledThumb = styled.div<StyledThumbProps>`
  display: flex;
  position: absolute;
  z-index: 1;
  left: -${SliderConstants.THUMB_SIZE / 2}px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  width: ${SliderConstants.THUMB_SIZE}px;
  height: ${SliderConstants.THUMB_SIZE}px;
  border: ${(props) =>
      props.theme.brand.elements.inputArea.drivingProfileSlider?.thumb
        .borderSize ?? props.theme.internal.size.border.heavy}
    solid
    ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.border};
  border-radius: 100%;
  background-color: ${(props) =>
    props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.background};
  color: ${(props) =>
    props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.text ??
    'inherit'};
  cursor: grab;

  & > svg {
    color: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.text ??
      'inherit'} !important;
  }

  ${(props) => props.position}: -${SliderConstants.THUMB_SIZE / 2}px;

  /* increase touch area */
  ::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300%;
    height: 150%;
    transform: translate(-50%, -50%);
  }

  :focus {
    border-color: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
        .focusBorderColor ||
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.border};
    outline: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
        .focusOutline || 'none'};
    background-color: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
        .focusBackground};
    color: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.focusText};

    & > svg {
      color: ${(props) =>
        props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
          .focusIconColor ?? 'inherit'} !important;
    }
  }

  :hover {
    border-color: ${(props) =>
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
        .hoverBorderColor ??
      props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.background};

    & > svg {
      color: ${(props) =>
        props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
          .hoverIconColor ?? 'inherit'} !important;
    }

    ::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 100px;
      border-color: ${(props) =>
        props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
          .focusBorderColor ||
        props.theme.brand.colors.inputArea.drivingProfileSlider.thumb.border};
      background-color: ${(props) =>
        props.theme.internal.hoverIndicatorColor.filledElement.default};

      & > svg {
        color: ${(props) =>
          props.theme.brand.colors.inputArea.drivingProfileSlider.thumb
            .focusIconColor ?? 'inherit'} !important;
      }
    }
  }

  :active {
    cursor: grabbing;
  }
`;
